import {
	Alert,
	AlertTitle,
	Checkbox,
	FormControlLabel,
	Typography,
} from "@mui/material";
import { format } from "date-fns";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useDateFnsLocale } from "../../../../../utils/date-locale";
import { dateKeyToLocalDate } from "../../../../holidays/utils/holiday-dates";
import { CopyShiftHoliday } from "./controllers/use-copy-shift-holidays";

interface HolidayAlertProps {
	// One per listed shift whose copy lands on a holiday
	holidays: CopyShiftHoliday[];
	// True when none of those shifts is selected for copying
	isSkipping: boolean;
	// Ticking deselects the holiday shifts; unticking selects them again
	onSkipChange: (isSkipping: boolean) => void;
}

/**
 * Banner: some copied shifts land on holidays in the target week. They are
 * copied unless the supervisor skips them here (or deselects them).
 */
const HolidayAlert: React.FC<HolidayAlertProps> = ({
	holidays,
	isSkipping,
	onSkipChange,
}) => {
	const { t } = useTranslation();
	const dateLocale = useDateFnsLocale();

	const details = useMemo(() => {
		// One line per holiday date + location, however many shifts land on it
		const seen = new Map<string, string>();
		holidays.forEach(({ dateKey, holidays: onDate, locationName }) => {
			const key = `${dateKey}|${locationName}`;
			if (seen.has(key)) return;
			seen.set(
				key,
				t("holidays_copy-warning-item", {
					date: format(dateKeyToLocalDate(dateKey), "EEE, MMM d", {
						locale: dateLocale,
					}),
					name: onDate.map((holiday) => holiday.name).join(", "),
					location: locationName,
				})
			);
		});
		return [...seen.entries()]
			.sort(([a], [b]) => a.localeCompare(b))
			.map(([, line]) => line);
	}, [holidays, t, dateLocale]);

	if (holidays.length === 0) return null;

	return (
		<Alert severity="warning" sx={{ mt: 1 }}>
			<AlertTitle>
				{t("holidays_copy-warning-title", { count: holidays.length })}
			</AlertTitle>
			<Typography sx={{ fontSize: "14px" }}>{details.join("; ")}.</Typography>
			<Typography sx={{ fontSize: "14px", fontWeight: 500, mt: 1 }}>
				{isSkipping
					? t("holidays_copy-outcome-skip", { count: holidays.length })
					: t("holidays_copy-outcome-keep", { count: holidays.length })}
			</Typography>
			<FormControlLabel
				sx={{ mt: 0.5, "& .MuiFormControlLabel-label": { fontSize: "14px" } }}
				control={
					<Checkbox
						size="small"
						checked={isSkipping}
						onChange={(e) => onSkipChange(e.target.checked)}
					/>
				}
				label={t("holidays_repeat-skip-label")}
			/>
		</Alert>
	);
};

export default HolidayAlert;
